import * as React from 'react';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridToolbarExport,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, TextField, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { SaveAlt as SaveAltIcon, Refresh, Search } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { InputAdornment } from "@mui/material";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


const LoggedInHistory = () => {
  const [rows, setRows] = useState([]);
  const user = useSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser  , setSelectedUser  ] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [showFilters, setShowFilters] = useState(false); // State to control filter visibility

  const columns = [
    { field: 'user_id', headerName: 'User   ID', width: 90 },
    { field: 'username', headerName: 'User   Name', width: 150 },
    { field: 'department', headerName: 'Department', width: 150 },
    { field: 'role', headerName: 'Role', width: 150 },
    { field: 'login_count', headerName: 'Login Count', width: 150 },
    { field: 'logout_count', headerName: 'Logout Count', width: 150 },
    { field: 'latest_login_time', headerName: 'Login Date, Time', width: 180 },
    { field: 'latest_logout_time', headerName: 'Logout Date, Time', width: 180 },
  ];

  const fetchLoggedInHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_USERMGMT_URL}/loggedInHistory`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      });
      const data = await response.json();
      const formattedData = data.map(record => ({
        ...record,
        latest_login_time: record.latest_login_time ? new Date(record.latest_login_time).toLocaleString() : '',
        latest_logout_time: record.latest_logout_time ? new Date(record.latest_logout_time).toLocaleString() : '',
      }));
      setRows(formattedData);
      setUniqueUsers([...new Set(formattedData.map(record => record.username))]);
      setUniqueDepartments([...new Set(formattedData.map(record => record.department))]);
      setUniqueRoles([...new Set(formattedData.map(record => record.role))]);
    } catch (error) {
      console.error('Error fetching logged-in history:', error);
    }
  };

  useEffect(() => {
    fetchLoggedInHistory();
  }, []);

  const clearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setSelectedUser  ("");
    setSelectedDepartment("");
    setSelectedRole("");
  };

  const filteredRows = rows.filter(row => {
    const matchesSearch = Object.values(row).some(value =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesUser   = !selectedUser   || row.username === selectedUser  ;
    const matchesDepartment = !selectedDepartment || row.department === selectedDepartment;
    const matchesRole = !selectedRole || row.role === selectedRole;
    const matchesDate = (!startDate || new Date(row.latest_login_time) >= new Date(startDate)) &&
                        (!endDate || new Date(row.latest_logout_time) <= new Date(endDate));
    return matchesSearch && matchesUser   && matchesDepartment && matchesRole && matchesDate;
  });

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('LoggedInHistory');

    // Add title row
    const titleRow = worksheet.addRow(['ARYAN EXPORTERS PVT. LTD.']);
    worksheet.mergeCells(`A1:H1`); // Adjust the range based on the number of columns
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
    const mergedTitleCell = worksheet.getCell('A1');
    mergedTitleCell.font = { bold: true, size: 14 };

    // Add summary row
    const summaryRow = worksheet.addRow([`Summary Of Logged In History ${startDate && endDate ? `from ${startDate} to ${endDate}` : startDate ? `on ${startDate}` : 'for all dates'}`]);
    worksheet.mergeCells(`A2:H2`); // Adjust the range based on the number of columns
    summaryRow.alignment = { horizontal: 'center', vertical: 'middle' };
    const mergedSummaryCell = worksheet.getCell('A2');
    mergedSummaryCell.font = { bold: true, size: 12 };

    // Add header row
    worksheet.addRow(['User  ID', 'User  Name', 'Department', 'Role', 'Login Count', 'Logout Count', 'Login Time', 'Logout Time']).font = { bold: true };

    // Add data rows
    filteredRows.forEach(row => {
        worksheet.addRow([
            row.user_id,
            row.username,
            row.department,
            row.role,
            row.login_count,
            row.logout_count,
            row.latest_login_time,
            row.latest_logout_time,
        ]);
    });

    // Apply styles to the worksheet
    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            // Apply border to all cells
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Bold for header rows and title/summary rows
        if (rowNumber === 1 || rowNumber === 2) {
            row.font = { bold: true };
        }
    });

    // Set column widths
    worksheet.getColumn(1).width = 10;  // User ID
    worksheet.getColumn(2).width = 20;  // User Name
    worksheet.getColumn(3).width = 80;  // Department
    worksheet.getColumn(4).width = 15;  // Role
    worksheet.getColumn(5).width = 15;  // Login Count
    worksheet.getColumn(6).width = 15;  // Logout Count
    worksheet.getColumn(7).width = 25;  // Login Time
    worksheet.getColumn(8).width = 25;  // Logout Time

    // Save the workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'LoggedInHistory.xlsx');
};

  return (
    <Box
      sx={{
        height: 480,
        width: '95%',
        marginLeft: "180px",
        paddingRight: "90px",
        marginTop: "0px"
      }}
    >
      <Box sx={{ marginBottom: "15px", display: "flex", gap: "10px", alignItems: "center" }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: '#ffffff',
            width: '1200px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#00308F',
              },
              '&:hover fieldset': {
                borderColor: '#00207E',
              },
            },
          }}
        />
        
        <Button onClick={() => setShowFilters(!showFilters)} startIcon={<FilterListIcon />}>
          Filter
        </Button>
        
        <Button onClick={handleExport} startIcon={<SaveAltIcon />}>
          Export
        </Button>
        <IconButton
          onClick={() => window.location.reload()} // Refresh the page
          color="primary"
          sx={{
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      {/* Conditional rendering of additional filter fields */}
      {showFilters && (
        <Box sx={{ marginBottom: "10px", display: "flex", gap: "10px", alignItems: "center" }}>
          <TextField
            label="From"
            variant="outlined"
            size="small"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            variant="outlined"
            size="small"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={selectedUser  }
              onChange={(e) => setSelectedUser  (e.target.value)}
              label="User  "
            >
                <MenuItem key='' value=''>
                No selection
                </MenuItem>
              {uniqueUsers.map((user) => (
                <MenuItem key={user} value={user}>
                  {user}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              label="Department"
            >
                <MenuItem key='' value=''>
                No selection
                </MenuItem>
              {uniqueDepartments.map((department) => (
                <MenuItem key={department} value={department}>
                  {department}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Role"
            >
                <MenuItem key='' value=''>
                No selection
                </MenuItem>
              {uniqueRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
      )}

      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        disableMultipleRowSelection
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
        getRowId={(row) => row.user_id} // Use user_id as the unique identifier
        components={{
          Toolbar: () => (
            <GridToolbarContainer>
              <GridToolbarExport />
            </GridToolbarContainer>
          ),
        }}
        sx={{
          boxShadow: 1,
          border: 1,
          borderColor: 'primary.light',
          typography: 'subtitle2',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.main',
          },
        }}
      />
    </Box>
  );
};

export default LoggedInHistory;
